import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  Storage as NormIcon,
  ImageSearch as ImageIcon,
  Memory as EmbedIcon,
  AccountTree as Neo4jIcon,
  List as JobsIcon,
} from '@mui/icons-material';
import { pipelineApi, statsApi } from '../../services/api';

const jobStatusColors = {
  pending: 'warning',
  running: 'info',
  completed: 'success',
  failed: 'error',
};

const jobStatusIcons = {
  pending: <PendingIcon />,
  running: <RefreshIcon />,
  completed: <SuccessIcon />,
  failed: <ErrorIcon />,
};

// ============================================================================
// JobCard Component
// ============================================================================
function JobCard({ job, onRefresh }) {
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await onRefresh(job.id);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
            {job.job_type.toUpperCase()}
          </Typography>
          <Chip
            icon={jobStatusIcons[job.status]}
            label={job.status}
            color={jobStatusColors[job.status]}
            size="small"
          />
          {job.status === 'running' && (
            <Tooltip title="Refresh status">
              <IconButton size="small" onClick={handleRefresh} disabled={refreshing}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {job.retailer_id && (
          <Typography variant="body2" color="text.secondary">
            Retailer: {job.retailer_id}
          </Typography>
        )}
        {job.normalized_brand && (
          <Typography variant="body2" color="text.secondary">
            Brand: {job.normalized_brand}
          </Typography>
        )}
        {job.force_mode && (
          <Chip label="Force Mode" size="small" color="warning" sx={{ mt: 1 }} />
        )}

        {job.status === 'running' && job.progress_percent !== undefined && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">
                {job.products_processed?.toLocaleString() || 0} / {job.products_total?.toLocaleString() || '?'} products
              </Typography>
              <Typography variant="body2">{job.progress_percent}%</Typography>
            </Box>
            <LinearProgress variant="determinate" value={job.progress_percent} />
          </Box>
        )}

        {job.error_message && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {job.error_message}
          </Alert>
        )}

        {job.status === 'completed' && job.result_json && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'success.light', borderRadius: 1 }}>
            {job.result_json.mappings_applied !== undefined && (
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.dark' }}>
                Mappings Applied: {job.result_json.mappings_applied}
              </Typography>
            )}
            {job.result_json.by_attribute && Object.keys(job.result_json.by_attribute).length > 0 && (
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">By attribute:</Typography>
                {Object.entries(job.result_json.by_attribute).map(([attr, count]) => (
                  <Chip
                    key={attr}
                    label={`${attr}: ${typeof count === 'object' ? count.refreshed || count.mapped || 0 : count}`}
                    size="small"
                    sx={{ ml: 0.5, mt: 0.5 }}
                    color="success"
                    variant="outlined"
                  />
                ))}
              </Box>
            )}
          </Box>
        )}

        {job.status === 'completed' && !job.result_json && job.products_processed > 0 && (
          <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
            Processed: {job.products_processed.toLocaleString()} products
          </Typography>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Created: {new Date(job.created_at).toLocaleString()}
        </Typography>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// EligibleCount Component
// ============================================================================
function EligibleCount({ phase, mode, retailerId, brand, attribute }) {
  const [count, setCount] = useState(null);
  const [byAttribute, setByAttribute] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [latestUpdate, setLatestUpdate] = useState(null);
  const [totalWithEmbeddings, setTotalWithEmbeddings] = useState(null);
  const [totalInNeo4j, setTotalInNeo4j] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCount = async () => {
      setLoading(true);
      try {
        const params = { mode };
        if (retailerId) params.retailer_id = retailerId;
        if (brand) params.brand = brand;
        if (attribute) params.attribute = attribute;

        const result = await pipelineApi.getCounts(phase, params);
        setCount(result.eligible_products);
        setByAttribute(result.by_attribute || null);
        setBreakdown(result.breakdown || null);
        setLatestUpdate(result.latest_embedding_update || null);
        setTotalWithEmbeddings(result.total_with_embeddings || null);
        setTotalInNeo4j(result.total_in_neo4j || null);
      } catch (err) {
        console.error('Failed to fetch count:', err);
        setCount(null);
        setBreakdown(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCount();
  }, [phase, mode, retailerId, brand, attribute]);

  if (loading) {
    return <CircularProgress size={16} sx={{ ml: 1 }} />;
  }

  if (count === null) {
    return null;
  }

  // Format timestamp for display
  const formatTimestamp = (ts) => {
    if (!ts) return 'Never';
    const date = new Date(ts);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // For Phase 1 (identity), show breakdown
  if (phase === 'phase1' && breakdown) {
    return (
      <Box>
        <Chip
          label={`${count.toLocaleString()} eligible products`}
          size="small"
          color={count > 0 ? 'primary' : 'default'}
        />
        {count > 0 && (
          <Box sx={{ pl: 1, mt: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
            {(breakdown.has_brand_mapping_no_normalized > 0 || breakdown.has_brand_mapping > 0) && (
              <Typography variant="caption" sx={{ display: 'block', color: 'success.main' }}>
                • {(breakdown.has_brand_mapping_no_normalized || breakdown.has_brand_mapping || 0).toLocaleString()} have brand mapping
                {breakdown.has_brand_mapping_no_normalized !== undefined && ' (not normalized)'}
              </Typography>
            )}
            {breakdown.no_brand_mapping > 0 && (
              <Typography variant="caption" sx={{ display: 'block', color: 'error.main' }}>
                • {breakdown.no_brand_mapping.toLocaleString()} have no brand mapping (will be skipped)
              </Typography>
            )}
            {(breakdown.has_raw_product_id_no_normalized > 0 || breakdown.has_raw_product_id > 0) && (
              <Typography variant="caption" sx={{ display: 'block' }}>
                • {(breakdown.has_raw_product_id_no_normalized || breakdown.has_raw_product_id || 0).toLocaleString()} have raw product ID
                {breakdown.has_raw_product_id_no_normalized !== undefined && ' (not normalized)'}
              </Typography>
            )}
            {(breakdown.has_raw_product_name_no_normalized > 0 || breakdown.has_raw_product_name > 0) && (
              <Typography variant="caption" sx={{ display: 'block' }}>
                • {(breakdown.has_raw_product_name_no_normalized || breakdown.has_raw_product_name || 0).toLocaleString()} have raw product name
                {breakdown.has_raw_product_name_no_normalized !== undefined && ' (not normalized)'}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  }

  // For Phase 2 (colors), show color-only breakdown
  if (phase === 'phase2' && byAttribute) {
    const stats = byAttribute.color || Object.values(byAttribute)[0] || {};
    return (
      <Box>
        <Chip
          label={`${count.toLocaleString()} eligible products`}
          size="small"
          color={count > 0 ? 'primary' : 'default'}
        />
        {count > 0 && (
          <Box sx={{ pl: 1, mt: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
            {stats.needs_normalization > 0 && (
              <Typography variant="caption" sx={{ display: 'block' }}>
                • {stats.needs_normalization.toLocaleString()} need color normalization
              </Typography>
            )}
            {stats.has_mapping_no_normalized > 0 && (
              <Typography variant="caption" sx={{ display: 'block', color: 'success.main' }}>
                &nbsp;&nbsp;↳ {stats.has_mapping_no_normalized.toLocaleString()} have mapping ready
              </Typography>
            )}
            {stats.is_generic_no_normalized > 0 && (
              <Typography variant="caption" sx={{ display: 'block', color: 'info.main' }}>
                &nbsp;&nbsp;↳ {stats.is_generic_no_normalized.toLocaleString()} are generic values
              </Typography>
            )}
            {stats.needs_ai_suggestion > 0 && (
              <Typography variant="caption" sx={{ display: 'block', color: 'warning.main' }}>
                &nbsp;&nbsp;↳ {stats.needs_ai_suggestion.toLocaleString()} need AI suggestion
              </Typography>
            )}
            {stats.pending_review > 0 && (
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                • {stats.pending_review.toLocaleString()} pending review
              </Typography>
            )}
            {stats.already_normalized > 0 && (
              <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled' }}>
                • {stats.already_normalized.toLocaleString()} already normalized
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  }

  // For Phase 3 (descriptions), show verbose breakdown
  if (phase === 'phase3' && breakdown) {
    return (
      <Box>
        <Chip
          label={`${count.toLocaleString()} eligible products`}
          size="small"
          color={count > 0 ? 'primary' : 'default'}
        />
        {(breakdown.total_with_description > 0 || count > 0) && (
          <Box sx={{ pl: 1, mt: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ display: 'block' }}>
              • {(breakdown.total_with_description || 0).toLocaleString()} total products with description
            </Typography>
            {breakdown.already_normalized > 0 && (
              <Typography variant="caption" sx={{ display: 'block', color: 'success.main' }}>
                • {breakdown.already_normalized.toLocaleString()} already have normalized description
              </Typography>
            )}
            {breakdown.needs_processing > 0 && (
              <Typography variant="caption" sx={{ display: 'block', color: 'warning.main' }}>
                • {breakdown.needs_processing.toLocaleString()} need description processing
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  }

  // For Categories (AI normalization), show source breakdown
  if (phase === 'categories' && breakdown) {
    return (
      <Box>
        <Chip
          label={`${count.toLocaleString()} eligible products`}
          size="small"
          color={count > 0 ? 'primary' : 'default'}
        />
        {(breakdown.total_with_ai_source > 0 || count > 0) && (
          <Box sx={{ pl: 1, mt: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ display: 'block' }}>
              • {(breakdown.total_with_ai_source || 0).toLocaleString()} total with AI-extracted category
            </Typography>
            {breakdown.has_description_subcategory > 0 && (
              <Typography variant="caption" sx={{ display: 'block', color: 'info.main' }}>
                &nbsp;&nbsp;↳ {breakdown.has_description_subcategory.toLocaleString()} from descriptions
              </Typography>
            )}
            {breakdown.has_image_category > 0 && (
              <Typography variant="caption" sx={{ display: 'block', color: 'secondary.main' }}>
                &nbsp;&nbsp;↳ {breakdown.has_image_category.toLocaleString()} from image analysis
              </Typography>
            )}
            {breakdown.already_normalized > 0 && (
              <Typography variant="caption" sx={{ display: 'block', color: 'success.main' }}>
                • {breakdown.already_normalized.toLocaleString()} already normalized
              </Typography>
            )}
            {breakdown.needs_normalization > 0 && (
              <Typography variant="caption" sx={{ display: 'block', color: 'warning.main' }}>
                • {breakdown.needs_normalization.toLocaleString()} need normalization
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  }

  // For Supplier Codes (Phase 3.5), show extraction breakdown
  if (phase === 'supplier_codes' && breakdown) {
    return (
      <Box>
        <Chip
          label={`${count.toLocaleString()} eligible products`}
          size="small"
          color={count > 0 ? 'primary' : 'default'}
        />
        {(breakdown.total_products > 0 || count > 0) && (
          <Box sx={{ pl: 1, mt: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ display: 'block' }}>
              • {(breakdown.total_products || 0).toLocaleString()} total active products with brand
            </Typography>
            {breakdown.has_style_code > 0 && (
              <Typography variant="caption" sx={{ display: 'block', color: 'success.main' }}>
                • {breakdown.has_style_code.toLocaleString()} have style code
              </Typography>
            )}
            {breakdown.missing_style_code > 0 && (
              <Typography variant="caption" sx={{ display: 'block', color: 'warning.main' }}>
                • {breakdown.missing_style_code.toLocaleString()} missing style code
              </Typography>
            )}
            {(breakdown.source_pattern > 0 || breakdown.source_brand_format > 0 || breakdown.source_heuristic > 0 || breakdown.source_failed > 0) && (
              <Box sx={{ pl: 1, mt: 0.5 }}>
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 'bold' }}>
                  Extraction source:
                </Typography>
                {breakdown.source_pattern > 0 && (
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    ↳ {breakdown.source_pattern.toLocaleString()} from pattern rules
                  </Typography>
                )}
                {breakdown.source_brand_format > 0 && (
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    ↳ {breakdown.source_brand_format.toLocaleString()} from brand format
                  </Typography>
                )}
                {breakdown.source_heuristic > 0 && (
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    ↳ {breakdown.source_heuristic.toLocaleString()} from heuristic (unvalidated)
                  </Typography>
                )}
                {breakdown.source_failed > 0 && (
                  <Typography variant="caption" sx={{ display: 'block', color: 'error.main' }}>
                    ↳ {breakdown.source_failed.toLocaleString()} extraction failed
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  }

  // For Image Analysis, show mode-specific breakdown
  if (phase === 'image_analysis' && breakdown) {
    const modeLabel = mode === 'backfill'
      ? 'products need backfill'
      : mode === 'force_all'
        ? 'products with images'
        : 'products never analyzed';

    return (
      <Box>
        <Chip
          label={`${count.toLocaleString()} ${modeLabel}`}
          size="small"
          color={count > 0 ? (mode === 'backfill' ? 'warning' : 'primary') : 'default'}
        />
        <Box sx={{ pl: 1, mt: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
          <Typography variant="caption" sx={{ display: 'block' }}>
            • {(breakdown.total_with_image || 0).toLocaleString()} total products with images
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: 'success.main' }}>
            • {(breakdown.already_analyzed || 0).toLocaleString()} already analyzed
          </Typography>
          {breakdown.never_analyzed > 0 && (
            <Typography variant="caption" sx={{ display: 'block', color: 'info.main' }}>
              • {breakdown.never_analyzed.toLocaleString()} never analyzed
            </Typography>
          )}
          {breakdown.needs_backfill > 0 && (
            <Typography variant="caption" sx={{ display: 'block', color: 'warning.main' }}>
              • {breakdown.needs_backfill.toLocaleString()} need backfill (missing extractions)
            </Typography>
          )}
          {mode === 'backfill' && breakdown.needs_backfill > 0 && (
            <Box sx={{ pl: 2 }}>
              {breakdown.missing_colors > 0 && (
                <Typography variant="caption" sx={{ display: 'block' }}>
                  ↳ {breakdown.missing_colors.toLocaleString()} missing colors
                </Typography>
              )}
              {breakdown.missing_pattern > 0 && (
                <Typography variant="caption" sx={{ display: 'block' }}>
                  ↳ {breakdown.missing_pattern.toLocaleString()} missing pattern
                </Typography>
              )}
              {breakdown.missing_category > 0 && (
                <Typography variant="caption" sx={{ display: 'block' }}>
                  ↳ {breakdown.missing_category.toLocaleString()} missing category
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  // For Sync Mappings (refresh_mappings), show color-only breakdown
  if (phase === 'refresh_mappings' && byAttribute) {
    const stats = byAttribute.color || Object.values(byAttribute)[0] || {};
    return (
      <Box>
        <Chip
          label={`${count.toLocaleString()} products with stale color mappings`}
          size="small"
          color={count > 0 ? 'warning' : 'success'}
        />
        {count > 0 && (
          <Box sx={{ pl: 1, mt: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ display: 'block' }}>
              • {(stats.total_stale || 0).toLocaleString()} need sync
            </Typography>
            {stats.mapping_changed > 0 && (
              <Typography variant="caption" sx={{ display: 'block', color: 'warning.main' }}>
                &nbsp;&nbsp;↳ {stats.mapping_changed.toLocaleString()} mapping table updated
              </Typography>
            )}
            {stats.brand_changed > 0 && (
              <Typography variant="caption" sx={{ display: 'block', color: 'info.main' }}>
                &nbsp;&nbsp;↳ {stats.brand_changed.toLocaleString()} brand re-normalized
              </Typography>
            )}
            {stats.brand_specific_upgrade > 0 && (
              <Typography variant="caption" sx={{ display: 'block', color: 'success.main' }}>
                &nbsp;&nbsp;↳ {stats.brand_specific_upgrade.toLocaleString()} have brand-specific mapping available
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  }

  // For embeddings phase, show detailed breakdown
  if (phase === 'embeddings' && breakdown) {
    const hasStale = breakdown.stale_color > 0 || breakdown.stale_category > 0 ||
                     breakdown.stale_pattern > 0 || breakdown.stale_brand > 0 ||
                     breakdown.stale_image_analysis > 0;

    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Chip
            label={`${count.toLocaleString()} need processing`}
            size="small"
            color={count > 0 ? 'warning' : 'success'}
          />
          {totalWithEmbeddings !== null && (
            <Typography variant="caption" color="text.secondary">
              ({totalWithEmbeddings.toLocaleString()} Active products have embeddings)
            </Typography>
          )}
        </Box>

        {latestUpdate && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Last embedding update: {formatTimestamp(latestUpdate)}
          </Typography>
        )}

        {count > 0 && (
          <Box sx={{ pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
            {breakdown.missing > 0 && (
              <Typography variant="caption" sx={{ display: 'block', color: 'info.main' }}>
                • {breakdown.missing.toLocaleString()} missing embeddings (new products)
              </Typography>
            )}
            {hasStale && (
              <>
                <Typography variant="caption" sx={{ display: 'block', color: 'warning.main', mt: 0.5 }}>
                  Stale embeddings (normalized after last embed):
                </Typography>
                <Box sx={{ pl: 1 }}>
                  {breakdown.stale_category > 0 && (
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      • {breakdown.stale_category.toLocaleString()} category changed
                    </Typography>
                  )}
                  {breakdown.stale_color > 0 && (
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      • {breakdown.stale_color.toLocaleString()} color changed
                    </Typography>
                  )}
                  {breakdown.stale_pattern > 0 && (
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      • {breakdown.stale_pattern.toLocaleString()} pattern changed
                    </Typography>
                  )}
                  {breakdown.stale_brand > 0 && (
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      • {breakdown.stale_brand.toLocaleString()} brand changed
                    </Typography>
                  )}
                  {breakdown.stale_image_analysis > 0 && (
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      • {breakdown.stale_image_analysis.toLocaleString()} image re-analyzed
                    </Typography>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                  (counts may overlap - one product can be stale for multiple reasons)
                </Typography>
              </>
            )}
          </Box>
        )}
      </Box>
    );
  }

  // For Neo4j, show mode-specific counts
  if (phase === 'neo4j') {
    // Mode-specific labels and descriptions
    const getModeDescription = () => {
      if (mode === 'full_products') {
        return `${count.toLocaleString()} products will be re-imported to Neo4j`;
      } else if (mode === 'full_relationships') {
        return `${count.toLocaleString()} products will have relationships refreshed`;
      } else {
        return `${count.toLocaleString()} products need sync`;
      }
    };

    return (
      <Box>
        <Chip
          label={getModeDescription()}
          size="small"
          color={count > 0 ? (mode === 'incremental' ? 'primary' : 'warning') : 'success'}
        />
        {mode === 'incremental' && breakdown && (
          <Box sx={{ pl: 1, mt: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
            {breakdown.missing_from_neo4j > 0 && (
              <Typography variant="caption" sx={{ display: 'block', color: 'info.main' }}>
                • {breakdown.missing_from_neo4j.toLocaleString()} not in Neo4j
              </Typography>
            )}
            {breakdown.stale_in_neo4j > 0 && (
              <Typography variant="caption" sx={{ display: 'block', color: 'warning.main' }}>
                • {breakdown.stale_in_neo4j.toLocaleString()} stale in Neo4j
              </Typography>
            )}
            {breakdown.up_to_date > 0 && (
              <Typography variant="caption" sx={{ display: 'block', color: 'success.main' }}>
                • {breakdown.up_to_date.toLocaleString()} up to date
              </Typography>
            )}
          </Box>
        )}
        <Box sx={{ mt: 1 }}>
          {totalWithEmbeddings !== null && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {totalWithEmbeddings.toLocaleString()} products have embeddings
            </Typography>
          )}
          {totalInNeo4j !== null && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {totalInNeo4j.toLocaleString()} products in Neo4j
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  // Default display for other phases
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
      <Chip
        label={`${count.toLocaleString()} products`}
        size="small"
        color={count > 0 ? 'primary' : 'default'}
        variant="outlined"
      />
      {byAttribute && typeof Object.values(byAttribute)[0] === 'number' && (
        <Box sx={{ ml: 1, display: 'flex', gap: 0.5 }}>
          {Object.entries(byAttribute).map(([attr, attrCount]) => (
            <Chip
              key={attr}
              label={`${attr}: ${attrCount.toLocaleString()}`}
              size="small"
              variant="outlined"
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

// ============================================================================
// PhasePanel Component - Reusable for each normalization phase
// ============================================================================
function PhasePanel({
  phase,
  title,
  description,
  modes = ['incremental', 'force_all'],
  attributes = null,
  retailers,
  brands,
  selectedRetailer,
  setSelectedRetailer,
  selectedBrand,
  setSelectedBrand,
  onTrigger,
  loading,
  filtersLoading = false
}) {
  const [mode, setMode] = useState(modes[0] || 'incremental');
  const [selectedAttribute, setSelectedAttribute] = useState('');
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [forceCount, setForceCount] = useState(null);

  // Reset mode when modes prop changes (e.g., switching tabs)
  useEffect(() => {
    setMode(modes[0] || 'incremental');
  }, [modes]);

  const handleTrigger = async () => {
    const filters = { mode };
    if (selectedRetailer) filters.retailer_id = selectedRetailer;
    if (selectedBrand) filters.brand = selectedBrand;
    if (selectedAttribute) filters.attribute = selectedAttribute;
    if (mode === 'force_all') filters.force = true;

    // Show warning dialog for FORCE mode
    if (mode === 'force_all') {
      try {
        const params = { mode };
        if (selectedRetailer) params.retailer_id = selectedRetailer;
        if (selectedBrand) params.brand = selectedBrand;
        if (selectedAttribute) params.attribute = selectedAttribute;
        const result = await pipelineApi.getCounts(phase, params);
        setForceCount(result.eligible_products);
        setForceDialogOpen(true);
      } catch (err) {
        console.error('Failed to fetch count for force warning:', err);
        // Still show dialog with unknown count
        setForceCount(null);
        setForceDialogOpen(true);
      }
      return;
    }

    onTrigger(filters);
  };

  const handleForceConfirm = () => {
    setForceDialogOpen(false);
    const filters = { mode, force: true };
    if (selectedRetailer) filters.retailer_id = selectedRetailer;
    if (selectedBrand) filters.brand = selectedBrand;
    if (selectedAttribute) filters.attribute = selectedAttribute;
    onTrigger(filters);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {description}
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: (attributes && modes.length > 1) ? 3 : 4 }}>
          <FormControl fullWidth size="small">
            <InputLabel shrink>Retailer</InputLabel>
            <Select
              value={selectedRetailer}
              label="Retailer"
              displayEmpty
              onChange={(e) => {
                setSelectedRetailer(e.target.value);
                setSelectedBrand('');
              }}
              disabled={filtersLoading}
              notched
            >
              <MenuItem value="">All Retailers {filtersLoading && '(Loading...)'}</MenuItem>
              {retailers.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name} ({r.product_count?.toLocaleString()})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: (attributes && modes.length > 1) ? 3 : 4 }}>
          <Tooltip
            title={phase === 'phase1' && mode === 'incremental'
              ? "Brand filter unavailable in incremental mode (products don't have normalized_brand yet)"
              : ''
            }
            placement="top"
          >
            <FormControl fullWidth size="small">
              <InputLabel shrink>Brand</InputLabel>
              <Select
                value={phase === 'phase1' && mode === 'incremental' ? '' : selectedBrand}
                label="Brand"
                displayEmpty
                onChange={(e) => setSelectedBrand(e.target.value)}
                disabled={filtersLoading || (phase === 'phase1' && mode === 'incremental')}
                notched
              >
                <MenuItem value="">
                  {phase === 'phase1' && mode === 'incremental'
                    ? 'N/A (use FORCE mode)'
                    : `All Brands ${filtersLoading ? '(Loading...)' : ''}`
                  }
                </MenuItem>
                {brands.map((b) => (
                  <MenuItem key={b.brand} value={b.brand}>
                    {b.brand} ({b.product_count?.toLocaleString()})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Tooltip>
        </Grid>
        {attributes && (
          <Grid size={{ xs: 12, md: modes.length > 1 ? 3 : 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel shrink>Attribute</InputLabel>
              <Select
                value={selectedAttribute}
                label="Attribute"
                displayEmpty
                onChange={(e) => setSelectedAttribute(e.target.value)}
                notched
              >
                <MenuItem value="">All Attributes</MenuItem>
                {attributes.map((attr) => (
                  <MenuItem key={attr} value={attr}>{attr}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        {modes.length > 1 && (
          <Grid size={{ xs: 12, md: attributes ? 3 : 4 }}>
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={(e, newMode) => newMode && setMode(newMode)}
              size="small"
              fullWidth
            >
              {modes.map((m) => (
                <ToggleButton key={m} value={m}>
                  {m === 'incremental' ? 'Incremental' : m === 'force_all' ? 'Force' : m === 'refresh_mappings' ? 'Sync' : 'Refresh'}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Grid>
        )}
      </Grid>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: 2, mt: 2 }}>
        <EligibleCount
          phase={mode === 'refresh_mappings' ? 'refresh_mappings' : phase}
          mode={mode}
          retailerId={selectedRetailer}
          brand={selectedBrand}
          attribute={selectedAttribute}
        />
        <Button
          variant="contained"
          startIcon={<PlayIcon />}
          onClick={handleTrigger}
          disabled={loading}
          color={mode === 'force_all' ? 'warning' : 'primary'}
          sx={{ minWidth: { xs: '100%', sm: 200 }, flexShrink: { xs: 1, sm: 0 } }}
        >
          Run {title}
        </Button>
      </Box>

      {/* FORCE Mode Warning Dialog */}
      <Dialog
        open={forceDialogOpen}
        onClose={() => setForceDialogOpen(false)}
      >
        <DialogTitle sx={{ color: 'warning.main' }}>
          Confirm FORCE Mode
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {forceCount !== null ? (
              <>
                You are about to run <strong>{title}</strong> in <strong>FORCE</strong> mode.
                This will reprocess <strong>{forceCount.toLocaleString()}</strong> products,
                regardless of whether they already have normalized values.
              </>
            ) : (
              <>
                You are about to run <strong>{title}</strong> in <strong>FORCE</strong> mode.
                This will reprocess all eligible products, regardless of whether they already have normalized values.
              </>
            )}
          </DialogContentText>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This operation may be slow and resource-intensive. Use incremental mode for normal operations.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForceDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleForceConfirm} color="warning" variant="contained">
            Confirm FORCE
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ============================================================================
// NormalizationTab Component
// ============================================================================
function NormalizationTab({ retailers, brands, selectedRetailer, setSelectedRetailer, selectedBrand, setSelectedBrand, onTrigger, loading, filtersLoading }) {
  const [subTab, setSubTab] = useState(0);

  const phases = [
    {
      phase: 'phase1',
      title: 'Brand, Product ID & Name',
      description: 'Normalizes brand, product_id, and product_name. Brand must be normalized first for subsequent steps.',
      triggerFn: (f) => pipelineApi.triggerPhase(1, f),
    },
    {
      phase: 'phase2',
      title: 'Colors',
      description: 'Normalizes colors with brand-aware mapping.',
      triggerFn: (f) => f.mode === 'refresh_mappings'
        ? pipelineApi.triggerRefreshMappings(f)
        : pipelineApi.triggerPhase(2, f),
      modes: ['incremental', 'force_all', 'refresh_mappings'],
    },
    {
      phase: 'phase3',
      title: 'Descriptions',
      description: 'Generates normalized descriptions for products.',
      triggerFn: (f) => pipelineApi.triggerPhase(3, f),
    },
    {
      phase: 'categories',
      title: 'Categories',
      description: 'Normalizes categories using AI-extracted values from descriptions and image analysis.',
      triggerFn: (f) => pipelineApi.triggerAINormalization(f),
    },
    {
      phase: 'supplier_codes',
      title: 'Supplier Codes',
      description: 'Extracts style codes and variant codes from product data using configured patterns. No AI calls — uses pre-configured rules from the Code Extraction dashboard.',
      triggerFn: (f) => pipelineApi.triggerSupplierCodes(f),
    },
  ];

  const currentPhase = phases[subTab];

  const handleTrigger = async (filters) => {
    onTrigger(currentPhase.triggerFn, currentPhase.title, filters);
  };

  return (
    <Box>
      <Tabs
        value={subTab}
        onChange={(e, v) => setSubTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab label="Brand, Product ID & Name" />
        <Tab label="Colors" />
        <Tab label="Descriptions" />
        <Tab label="Categories" />
        <Tab label="Supplier Codes" />
      </Tabs>

      <PhasePanel
        phase={currentPhase.phase}
        title={currentPhase.title}
        description={currentPhase.description}
        modes={currentPhase.modes || ['incremental', 'force_all']}
        attributes={currentPhase.attributes}
        retailers={retailers}
        brands={brands}
        selectedRetailer={selectedRetailer}
        setSelectedRetailer={setSelectedRetailer}
        selectedBrand={selectedBrand}
        setSelectedBrand={setSelectedBrand}
        onTrigger={handleTrigger}
        loading={loading}
        filtersLoading={filtersLoading}
      />
    </Box>
  );
}

// ============================================================================
// ImageAnalysisTab Component
// ============================================================================
function ImageAnalysisTab({ retailers, brands, selectedRetailer, setSelectedRetailer, selectedBrand, setSelectedBrand, onTrigger, loading, filtersLoading }) {
  const [mode, setMode] = useState('incremental');
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [forceCount, setForceCount] = useState(null);

  const handleTrigger = async () => {
    const filters = { mode };
    if (selectedRetailer) filters.retailer_id = selectedRetailer;
    if (selectedBrand) filters.brand = selectedBrand;

    // Show warning dialog for FORCE mode
    if (mode === 'force_all') {
      try {
        const params = { mode };
        if (selectedRetailer) params.retailer_id = selectedRetailer;
        if (selectedBrand) params.brand = selectedBrand;
        const result = await pipelineApi.getCounts('image_analysis', params);
        setForceCount(result.eligible_products);
        setForceDialogOpen(true);
      } catch (err) {
        console.error('Failed to fetch count for force warning:', err);
        setForceCount(null);
        setForceDialogOpen(true);
      }
      return;
    }

    onTrigger(pipelineApi.triggerImageAnalysis, 'Image Analysis', filters);
  };

  const handleForceConfirm = () => {
    setForceDialogOpen(false);
    const filters = { mode: 'force_all' };
    if (selectedRetailer) filters.retailer_id = selectedRetailer;
    if (selectedBrand) filters.brand = selectedBrand;
    onTrigger(pipelineApi.triggerImageAnalysis, 'Image Analysis', filters);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>AI Image Analysis</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Uses OpenAI Vision to extract colors, patterns, and categories from product images.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <FormControl fullWidth size="small">
            <InputLabel shrink>Retailer</InputLabel>
            <Select
              value={selectedRetailer}
              label="Retailer"
              displayEmpty
              onChange={(e) => {
                setSelectedRetailer(e.target.value);
                setSelectedBrand('');
              }}
              disabled={filtersLoading}
              notched
            >
              <MenuItem value="">All Retailers {filtersLoading && '(Loading...)'}</MenuItem>
              {retailers.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name} ({r.product_count?.toLocaleString()})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <FormControl fullWidth size="small">
            <InputLabel shrink>Brand</InputLabel>
            <Select
              value={selectedBrand}
              label="Brand"
              displayEmpty
              onChange={(e) => setSelectedBrand(e.target.value)}
              disabled={filtersLoading}
              notched
            >
              <MenuItem value="">All Brands {filtersLoading && '(Loading...)'}</MenuItem>
              {brands.map((b) => (
                <MenuItem key={b.brand} value={b.brand}>
                  {b.brand} ({b.product_count?.toLocaleString()})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(e, v) => v && setMode(v)}
            size="small"
            fullWidth
          >
            <ToggleButton value="incremental">Incremental</ToggleButton>
            <ToggleButton value="backfill">Backfill</ToggleButton>
            <ToggleButton value="force_all">Force</ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      </Grid>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        {mode === 'incremental' && 'Analyze products that have never been processed by image analysis.'}
        {mode === 'backfill' && 'Re-analyze products that were previously analyzed but are missing extracted colors, pattern, or category.'}
        {mode === 'force_all' && 'Re-analyze all products with images, regardless of previous analysis.'}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: 2, mt: 2 }}>
        <EligibleCount
          phase="image_analysis"
          mode={mode}
          retailerId={selectedRetailer}
          brand={selectedBrand}
        />
        <Button
          variant="contained"
          startIcon={<PlayIcon />}
          onClick={handleTrigger}
          disabled={loading}
          color={mode === 'force_all' ? 'warning' : mode === 'backfill' ? 'info' : 'primary'}
          sx={{ minWidth: { xs: '100%', sm: 200 }, flexShrink: { xs: 1, sm: 0 } }}
        >
          Run Image Analysis
        </Button>
      </Box>

      {/* FORCE Mode Warning Dialog */}
      <Dialog
        open={forceDialogOpen}
        onClose={() => setForceDialogOpen(false)}
      >
        <DialogTitle sx={{ color: 'warning.main' }}>
          Confirm FORCE Mode
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {forceCount !== null ? (
              <>
                You are about to run <strong>Image Analysis</strong> in <strong>FORCE</strong> mode.
                This will re-analyze <strong>{forceCount.toLocaleString()}</strong> products
                using the OpenAI Vision API, regardless of whether they were previously analyzed.
              </>
            ) : (
              <>
                You are about to run <strong>Image Analysis</strong> in <strong>FORCE</strong> mode.
                This will re-analyze all products with images using the OpenAI Vision API.
              </>
            )}
          </DialogContentText>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This operation is expensive (OpenAI Vision API calls). Use incremental or backfill mode for normal operations.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForceDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleForceConfirm} color="warning" variant="contained">
            Confirm FORCE
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ============================================================================
// EmbeddingsTab Component
// ============================================================================
function EmbeddingsTab({ retailers, brands, selectedRetailer, setSelectedRetailer, selectedBrand, setSelectedBrand, onTrigger, loading, filtersLoading }) {
  const [force, setForce] = useState(false);
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [forceCount, setForceCount] = useState(null);

  const handleTrigger = async () => {
    const filters = {};
    if (selectedRetailer) filters.retailer_id = selectedRetailer;
    if (selectedBrand) filters.brand = selectedBrand;
    if (force) filters.force = true;

    // Show warning dialog for FORCE mode
    if (force) {
      try {
        const params = { mode: 'force_all' };
        if (selectedRetailer) params.retailer_id = selectedRetailer;
        if (selectedBrand) params.brand = selectedBrand;
        const result = await pipelineApi.getCounts('embeddings', params);
        setForceCount(result.total_with_embeddings || result.eligible_products);
        setForceDialogOpen(true);
      } catch (err) {
        console.error('Failed to fetch count for force warning:', err);
        setForceCount(null);
        setForceDialogOpen(true);
      }
      return;
    }

    onTrigger(pipelineApi.triggerEmbeddings, 'Embeddings', filters);
  };

  const handleForceConfirm = () => {
    setForceDialogOpen(false);
    const filters = {};
    if (selectedRetailer) filters.retailer_id = selectedRetailer;
    if (selectedBrand) filters.brand = selectedBrand;
    filters.force = true;
    onTrigger(pipelineApi.triggerEmbeddings, 'Embeddings', filters);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>Generate Embeddings</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Creates vector embeddings for product matching. Products need normalized values first.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <FormControl fullWidth size="small">
            <InputLabel shrink>Retailer</InputLabel>
            <Select
              value={selectedRetailer}
              label="Retailer"
              displayEmpty
              onChange={(e) => {
                setSelectedRetailer(e.target.value);
                setSelectedBrand('');
              }}
              disabled={filtersLoading}
              notched
            >
              <MenuItem value="">All Retailers {filtersLoading && '(Loading...)'}</MenuItem>
              {retailers.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name} ({r.product_count?.toLocaleString()})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <FormControl fullWidth size="small">
            <InputLabel shrink>Brand</InputLabel>
            <Select
              value={selectedBrand}
              label="Brand"
              displayEmpty
              onChange={(e) => setSelectedBrand(e.target.value)}
              disabled={filtersLoading}
              notched
            >
              <MenuItem value="">All Brands {filtersLoading && '(Loading...)'}</MenuItem>
              {brands.map((b) => (
                <MenuItem key={b.brand} value={b.brand}>
                  {b.brand} ({b.product_count?.toLocaleString()})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <ToggleButtonGroup
            value={force ? 'force' : 'incremental'}
            exclusive
            onChange={(e, v) => setForce(v === 'force')}
            size="small"
            fullWidth
          >
            <ToggleButton value="incremental">Incremental</ToggleButton>
            <ToggleButton value="force">Force</ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: 2, mt: 2 }}>
        <EligibleCount
          phase="embeddings"
          mode={force ? 'force_all' : 'incremental'}
          retailerId={selectedRetailer}
          brand={selectedBrand}
        />
        <Button
          variant="contained"
          startIcon={<PlayIcon />}
          onClick={handleTrigger}
          disabled={loading}
          color={force ? 'warning' : 'primary'}
          sx={{ minWidth: { xs: '100%', sm: 200 }, flexShrink: { xs: 1, sm: 0 } }}
        >
          Generate Embeddings
        </Button>
      </Box>

      {/* FORCE Mode Warning Dialog */}
      <Dialog
        open={forceDialogOpen}
        onClose={() => setForceDialogOpen(false)}
      >
        <DialogTitle sx={{ color: 'warning.main' }}>
          Confirm FORCE Mode
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {forceCount !== null ? (
              <>
                You are about to regenerate embeddings in <strong>FORCE</strong> mode.
                This will reprocess <strong>{forceCount.toLocaleString()}</strong> Active products
                that already have embeddings.
              </>
            ) : (
              <>
                You are about to regenerate embeddings in <strong>FORCE</strong> mode.
                This will reprocess all Active products with embeddings.
              </>
            )}
          </DialogContentText>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This operation is expensive (OpenAI API calls). Use incremental mode for normal operations.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForceDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleForceConfirm} color="warning" variant="contained">
            Confirm FORCE
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ============================================================================
// Neo4jTab Component
// ============================================================================
function Neo4jTab({ retailers, brands, selectedRetailer, setSelectedRetailer, selectedBrand, setSelectedBrand, onTrigger, loading, filtersLoading }) {
  const [mode, setMode] = useState('incremental');
  const [gtinDryRun, setGtinDryRun] = useState(true);
  const [gtinLoading, setGtinLoading] = useState(false);
  const [gtinResult, setGtinResult] = useState(null);

  const [familyResyncDryRun, setFamilyResyncDryRun] = useState(true);
  const [familyResyncLoading, setFamilyResyncLoading] = useState(false);
  const [familyResyncResult, setFamilyResyncResult] = useState(null);
  const [pendingResync, setPendingResync] = useState({ count: 0, categories: [] });
  const [pendingResyncLoading, setPendingResyncLoading] = useState(false);

  const fetchPendingResync = useCallback(async () => {
    setPendingResyncLoading(true);
    try {
      const data = await pipelineApi.getPendingCategoryFamilyResync();
      setPendingResync({
        count: data.count || 0,
        categories: data.categories || [],
      });
    } catch {
      setPendingResync({ count: 0, categories: [] });
    } finally {
      setPendingResyncLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingResync();
  }, [fetchPendingResync]);

  const handleFamilyResync = async () => {
    setFamilyResyncLoading(true);
    setFamilyResyncResult(null);
    try {
      const result = await pipelineApi.triggerCategoryFamilyResync({ dry_run: familyResyncDryRun });
      setFamilyResyncResult(result);
      if (!familyResyncDryRun) {
        await fetchPendingResync();
      }
    } catch (err) {
      setFamilyResyncResult({ error: err.message });
    } finally {
      setFamilyResyncLoading(false);
    }
  };

  const handleTrigger = () => {
    const filters = { mode };
    if (selectedRetailer) filters.retailer_id = selectedRetailer;
    if (selectedBrand) filters.brand = selectedBrand;
    onTrigger(pipelineApi.triggerNeo4jEnhanced, 'Neo4j Sync', filters);
  };

  const handleGtinSync = async () => {
    setGtinLoading(true);
    setGtinResult(null);
    try {
      const filters = { dry_run: gtinDryRun };
      if (selectedRetailer) filters.retailer_id = selectedRetailer;
      if (selectedBrand) filters.brand = selectedBrand;
      const result = await pipelineApi.triggerGtinSync(filters);
      setGtinResult(result);
    } catch (err) {
      setGtinResult({ error: err.message });
    } finally {
      setGtinLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>Neo4j Sync</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Syncs products and relationships to Neo4j graph database for matching.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <FormControl fullWidth size="small">
            <InputLabel shrink>Retailer</InputLabel>
            <Select
              value={selectedRetailer}
              label="Retailer"
              displayEmpty
              onChange={(e) => {
                setSelectedRetailer(e.target.value);
                setSelectedBrand('');
              }}
              disabled={filtersLoading}
              notched
            >
              <MenuItem value="">All Retailers {filtersLoading && '(Loading...)'}</MenuItem>
              {retailers.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name} ({r.product_count?.toLocaleString()})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <FormControl fullWidth size="small">
            <InputLabel shrink>Brand</InputLabel>
            <Select
              value={selectedBrand}
              label="Brand"
              displayEmpty
              onChange={(e) => setSelectedBrand(e.target.value)}
              disabled={filtersLoading}
              notched
            >
              <MenuItem value="">All Brands {filtersLoading && '(Loading...)'}</MenuItem>
              {brands.map((b) => (
                <MenuItem key={b.brand} value={b.brand}>
                  {b.brand} ({b.product_count?.toLocaleString()})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <ToggleButtonGroup
        value={mode}
        exclusive
        onChange={(e, v) => v && setMode(v)}
        size="small"
        fullWidth
        sx={{ mb: 2 }}
      >
        <ToggleButton value="incremental">
          Incremental
        </ToggleButton>
        <ToggleButton value="full_products">
          Full Products
        </ToggleButton>
        <ToggleButton value="full_relationships">
          Full Relationships
        </ToggleButton>
      </ToggleButtonGroup>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        {mode === 'incremental' && 'Only sync products that have changed since last sync.'}
        {mode === 'full_products' && 'Re-import all products to Neo4j (keeps relationships).'}
        {mode === 'full_relationships' && 'Refresh all relationships without re-importing products.'}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: 2, mb: 4 }}>
        <EligibleCount
          phase="neo4j"
          mode={mode}
          retailerId={selectedRetailer}
          brand={selectedBrand}
        />
        <Button
          variant="contained"
          startIcon={<PlayIcon />}
          onClick={handleTrigger}
          disabled={loading}
          sx={{ minWidth: { xs: '100%', sm: 200 }, flexShrink: { xs: 1, sm: 0 } }}
        >
          Run Neo4j Sync
        </Button>
      </Box>

      {/* GTIN Sync Section */}
      <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 3 }}>
        <Typography variant="h6" gutterBottom>
          GTIN Relationship Sync
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Creates IDENTICAL relationships for products sharing GTINs (barcodes).
          Requires embedding similarity ≥ 80% as a guardrail against bad GTIN data.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: 2 }}>
          <ToggleButtonGroup
            value={gtinDryRun ? 'dry_run' : 'execute'}
            exclusive
            onChange={(e, v) => v && setGtinDryRun(v === 'dry_run')}
            size="small"
          >
            <ToggleButton value="dry_run">
              Dry Run
            </ToggleButton>
            <ToggleButton value="execute">
              Execute
            </ToggleButton>
          </ToggleButtonGroup>

          <Typography variant="caption" color="text.secondary">
            {gtinDryRun
              ? 'Preview changes without modifying Neo4j'
              : 'Create/update GTIN relationships in Neo4j'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: 2 }}>
          <Box>
            {gtinResult && !gtinResult.error && (
              <Box sx={{
                p: 1.5,
                bgcolor: gtinDryRun ? 'info.light' : 'success.light',
                borderRadius: 1,
                maxWidth: { xs: '100%', sm: 400 }
              }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: gtinDryRun ? 'info.dark' : 'success.dark' }}>
                  {gtinResult.mode === 'DRY_RUN' ? 'Dry Run Results' : 'Sync Complete'}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    GTIN Direct: {gtinResult.gtin_direct?.created?.toLocaleString() || 0}
                    {gtinResult.mode === 'DRY_RUN' ? ' would be created' : ' created'}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    GTIN Transitive: {gtinResult.gtin_transitive?.created?.toLocaleString() || 0}
                    {gtinResult.mode === 'DRY_RUN' ? ' would be created' : ' created'}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    Hub Transitive: {gtinResult.hub_transitive?.created?.toLocaleString() || 0}
                    {gtinResult.mode === 'DRY_RUN' ? ' would be created' : ' created'}
                  </Typography>
                  {gtinResult.gtin_direct?.rejection_rate > 0 && (
                    <Typography variant="caption" sx={{ display: 'block', color: 'warning.main' }}>
                      Rejection rate: {(gtinResult.gtin_direct.rejection_rate * 100).toFixed(1)}%
                      (low embedding similarity)
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Duration: {gtinResult.duration_seconds?.toFixed(1)}s
                  </Typography>
                </Box>
              </Box>
            )}
            {gtinResult?.error && (
              <Alert severity="error" sx={{ maxWidth: { xs: '100%', sm: 400 } }}>
                {gtinResult.error}
              </Alert>
            )}
          </Box>

          <Button
            variant="contained"
            color={gtinDryRun ? 'info' : 'success'}
            startIcon={gtinLoading ? <CircularProgress size={20} color="inherit" /> : <PlayIcon />}
            onClick={handleGtinSync}
            disabled={gtinLoading || loading}
            sx={{ minWidth: { xs: '100%', sm: 200 }, flexShrink: { xs: 1, sm: 0 } }}
          >
            {gtinDryRun ? 'Preview GTIN Sync' : 'Run GTIN Sync'}
          </Button>
        </Box>
      </Box>

      {/* Category Family Resync Section */}
      <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 3, mt: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="h6">Category Family Resync</Typography>
          <Chip
            size="small"
            label={pendingResyncLoading ? '...' : `${pendingResync.count} pending`}
            color={pendingResync.count > 0 ? 'warning' : 'default'}
          />
          <Tooltip title="Refresh pending count">
            <IconButton size="small" onClick={fetchPendingResync} disabled={pendingResyncLoading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          When a category type is reassigned to a different family, products in that category
          may have stale IDENTICAL/VARIANT relationships in Neo4j. This recomputes only their
          relationships — embeddings are not regenerated.
        </Typography>

        {pendingResync.count > 0 && (
          <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {pendingResync.categories.slice(0, 20).map((cat) => (
              <Chip key={cat} size="small" label={cat} variant="outlined" />
            ))}
            {pendingResync.categories.length > 20 && (
              <Chip size="small" label={`+${pendingResync.categories.length - 20} more`} variant="outlined" />
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: 2 }}>
          <ToggleButtonGroup
            value={familyResyncDryRun ? 'dry_run' : 'execute'}
            exclusive
            onChange={(e, v) => v && setFamilyResyncDryRun(v === 'dry_run')}
            size="small"
          >
            <ToggleButton value="dry_run">Dry Run</ToggleButton>
            <ToggleButton value="execute">Execute</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary">
            {familyResyncDryRun
              ? 'Preview affected products and edge counts without modifying Neo4j'
              : 'Delete and rebuild relationships for affected products, then clear pending flag'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: 2 }}>
          <Box>
            {familyResyncResult && !familyResyncResult.error && (
              <Box sx={{
                p: 1.5,
                bgcolor: familyResyncDryRun ? 'info.light' : 'success.light',
                borderRadius: 1,
                maxWidth: { xs: '100%', sm: 400 }
              }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: familyResyncDryRun ? 'info.dark' : 'success.dark' }}>
                  {familyResyncResult.status === 'noop'
                    ? 'No categories pending'
                    : familyResyncDryRun ? 'Dry Run Results' : 'Resync Complete'}
                </Typography>
                {familyResyncResult.stats && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      Categories: {familyResyncResult.stats.categories?.length || 0}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      Products in scope: {familyResyncResult.stats.product_count?.toLocaleString() || 0}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      IDENTICAL: -{familyResyncResult.stats.deleted?.identical?.toLocaleString() || 0} / +{familyResyncResult.stats.created?.identical?.toLocaleString() || 0}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      VARIANT: -{familyResyncResult.stats.deleted?.variant?.toLocaleString() || 0} / +{familyResyncResult.stats.created?.variant?.toLocaleString() || 0}
                    </Typography>
                    {familyResyncResult.cleared_pending_count > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Cleared {familyResyncResult.cleared_pending_count} pending flag{familyResyncResult.cleared_pending_count === 1 ? '' : 's'}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}
            {familyResyncResult?.error && (
              <Alert severity="error" sx={{ maxWidth: { xs: '100%', sm: 400 } }}>
                {familyResyncResult.error}
              </Alert>
            )}
          </Box>

          <Button
            variant="contained"
            color={familyResyncDryRun ? 'info' : 'success'}
            startIcon={familyResyncLoading ? <CircularProgress size={20} color="inherit" /> : <PlayIcon />}
            onClick={handleFamilyResync}
            disabled={familyResyncLoading || loading || pendingResync.count === 0}
            sx={{ minWidth: { xs: '100%', sm: 200 }, flexShrink: { xs: 1, sm: 0 } }}
          >
            {familyResyncDryRun ? 'Preview Resync' : 'Run Resync'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

// ============================================================================
// JobsTab Component
// ============================================================================
function JobsTab({ jobs, onRefresh, onRefreshJob }) {
  const [statusFilter, setStatusFilter] = useState('');

  const filteredJobs = statusFilter
    ? jobs.filter(j => j.status === statusFilter)
    : jobs;

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Pipeline Jobs
        </Typography>
        <FormControl size="small" sx={{ minWidth: 150, mr: 1 }}>
          <InputLabel shrink>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            displayEmpty
            onChange={(e) => setStatusFilter(e.target.value)}
            notched
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="running">Running</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
          </Select>
        </FormControl>
        <Tooltip title="Refresh">
          <IconButton onClick={onRefresh}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {filteredJobs.length === 0 ? (
        <Typography color="text.secondary">No jobs found</Typography>
      ) : (
        filteredJobs.map((job) => (
          <JobCard key={job.id} job={job} onRefresh={onRefreshJob} />
        ))
      )}
    </Box>
  );
}

// ============================================================================
// Main PipelineControls Component
// ============================================================================
export default function PipelineControls() {
  const [mainTab, setMainTab] = useState(0);
  const [retailers, setRetailers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedRetailer, setSelectedRetailer] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Load retailers once on mount
  useEffect(() => {
    const loadRetailers = async () => {
      try {
        const retailersData = await statsApi.getRetailers();
        setRetailers(retailersData || []);
      } catch (err) {
        console.error('Failed to load retailers:', err);
        setRetailers([]);
      }
    };
    loadRetailers();
  }, []);

  // Load brands when retailer selection changes
  useEffect(() => {
    const loadBrands = async () => {
      setFiltersLoading(true);
      try {
        const brandsData = await statsApi.getBrands(selectedRetailer || null);
        setBrands(brandsData || []);
      } catch (err) {
        console.error('Failed to load brands:', err);
        setBrands([]);
      } finally {
        setFiltersLoading(false);
      }
    };
    loadBrands();
  }, [selectedRetailer]);

  const loadJobs = useCallback(async () => {
    try {
      const data = await pipelineApi.getJobs({ limit: 20 });
      setJobs(data.jobs || []);
    } catch (err) {
      console.error('Failed to load jobs:', err);
    }
  }, []);

  const refreshJob = async (jobId) => {
    try {
      const updatedJob = await pipelineApi.getJob(jobId);
      setJobs((prev) =>
        prev.map((j) => (j.id === jobId ? updatedJob : j))
      );
    } catch (err) {
      console.error('Failed to refresh job:', err);
    }
  };

  // Load jobs on mount and poll for running jobs
  useEffect(() => {
    loadJobs();

    const interval = setInterval(() => {
      // Check jobs state via callback to avoid stale closure
      setJobs((currentJobs) => {
        const hasRunning = currentJobs.some((j) => j.status === 'running');
        if (hasRunning) {
          loadJobs();
        }
        return currentJobs;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [loadJobs]);

  const triggerPipeline = async (action, actionName, filters = {}) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await action(filters);
      setSuccess(`${actionName} triggered successfully. Job ID: ${result.job_id}`);
      loadJobs();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const tabProps = {
    retailers,
    brands,
    selectedRetailer,
    setSelectedRetailer,
    selectedBrand,
    setSelectedBrand,
    onTrigger: triggerPipeline,
    loading,
    filtersLoading,
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card>
        <Tabs
          value={mainTab}
          onChange={(e, v) => setMainTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab icon={<NormIcon />} label="Normalization" iconPosition="start" />
          <Tab icon={<ImageIcon />} label="Image Analysis" iconPosition="start" />
          <Tab icon={<EmbedIcon />} label="Embeddings" iconPosition="start" />
          <Tab icon={<Neo4jIcon />} label="Neo4j" iconPosition="start" />
          <Tab icon={<JobsIcon />} label="Jobs" iconPosition="start" />
        </Tabs>

        <CardContent>
          {mainTab === 0 && <NormalizationTab {...tabProps} />}
          {mainTab === 1 && <ImageAnalysisTab {...tabProps} />}
          {mainTab === 2 && <EmbeddingsTab {...tabProps} />}
          {mainTab === 3 && <Neo4jTab {...tabProps} />}
          {mainTab === 4 && (
            <JobsTab
              jobs={jobs}
              onRefresh={loadJobs}
              onRefreshJob={refreshJob}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
